export interface DocumentEntry {
  id: string;
  type: string;
  number: string;
  file: File | null;
  fileName: string;
}

export interface BasicInfoData {
  photo: File | null;
  photoPreview: string | null;
  title: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  dob: string;
  gender: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  documents: DocumentEntry[];
}

export interface QualificationEntry {
  id: string;
  qualification: string;
  collegeName: string;
}

export interface SpecialisationEntry {
  id: string;
  specialisation: string;
  yearsInArea: string;
}

export interface ExpertiseEntry {
  id: string;
  expertise: string;
}

export interface CertificationEntry {
  id: string;
  certification: string;
}

export interface EducationWorkData {
  qualifications: QualificationEntry[];
  workExperience: string;
  specialisations: SpecialisationEntry[];
  expertises: ExpertiseEntry[];
  certifications: CertificationEntry[];
  languages: [string, string, string, string];
}

export interface PermissionsState {
  actions: Record<string, boolean>;
  content: Record<
    string,
    { create: boolean; delete: boolean; edit: boolean; view: boolean }
  >;
}

export interface RoleReportingData {
  role: string;
  jobType: string;
  joiningDate: string;
  reportingManagerName: string;
  permissions: PermissionsState;
}

export interface AddNutritionistFormData {
  basicInfo: BasicInfoData;
  educationWork: EducationWorkData;
  roleReporting: RoleReportingData;
}

export interface BasicInfoErrors {
  title?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  dob?: string;
  gender?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
}

export const validateBasicInfo = (data: BasicInfoData): BasicInfoErrors => {
  const errors: BasicInfoErrors = {};

  if (!data.title)
    errors.title = "Title is required";

  if (!data.firstName.trim())
    errors.firstName = "First name is required";

  if (!data.lastName.trim())
    errors.lastName = "Last name is required";

  if (!data.phone.trim()) {
    errors.phone = "Phone number is required";
  } else if (!/^\d{10}$/.test(data.phone.replace(/[\s\-]/g, ""))) {
    errors.phone = "Enter a valid 10-digit phone number";
  }

  if (!data.email.trim()) {
    errors.email = "Email address is required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = "Enter a valid email address";
  }

  if (!data.dob)
    errors.dob = "Date of birth is required";

  if (!data.gender)
    errors.gender = "Gender is required";

  if (!data.address.trim())
    errors.address = "Address is required";

  if (!data.city.trim())
    errors.city = "City is required";

  if (!data.state)
    errors.state = "State is required";

  if (!data.pincode.trim()) {
    errors.pincode = "Pincode is required";
  } else if (!/^\d{6}$/.test(data.pincode)) {
    errors.pincode = "Pincode must be 6 digits";
  }

  return errors;
};

export interface EducationWorkErrors {
  qualifications?: Record<string, { qualification?: string; collegeName?: string }>;
  workExperience?: string;
  specialisations?: Record<string, { specialisation?: string; yearsInArea?: string }>;
  expertises?: Record<string, string>;
  certifications?: Record<string, string>;
  languages?: string;
}

export const validateEducationWork = (
  data: EducationWorkData,
): EducationWorkErrors => {
  const errors: EducationWorkErrors = {};

  const qualErrors: Record<string, { qualification?: string; collegeName?: string }> = {};
  data.qualifications.forEach((q) => {
    const rowErr: { qualification?: string; collegeName?: string } = {};
    if (!q.qualification) rowErr.qualification = "Qualification is required";
    if (!q.collegeName.trim()) rowErr.collegeName = "College name is required";
    if (rowErr.qualification || rowErr.collegeName) qualErrors[q.id] = rowErr;
  });
  if (Object.keys(qualErrors).length > 0) errors.qualifications = qualErrors;

  if (!data.workExperience.trim())
    errors.workExperience = "Work experience is required";

  const specErrors: Record<string, { specialisation?: string; yearsInArea?: string }> = {};
  data.specialisations.forEach((s) => {
    const rowErr: { specialisation?: string; yearsInArea?: string } = {};
    if (!s.specialisation) rowErr.specialisation = "Specialisation is required";
    if (!s.yearsInArea.trim()) rowErr.yearsInArea = "Years in this area is required";
    if (rowErr.specialisation || rowErr.yearsInArea) specErrors[s.id] = rowErr;
  });
  if (Object.keys(specErrors).length > 0) errors.specialisations = specErrors;

  const expErrors: Record<string, string> = {};
  data.expertises.forEach((e) => {
    if (!e.expertise) expErrors[e.id] = "Expertise is required";
  });
  if (Object.keys(expErrors).length > 0) errors.expertises = expErrors;

  const certErrors: Record<string, string> = {};
  data.certifications.forEach((c) => {
    if (!c.certification) certErrors[c.id] = "Certification is required";
  });
  if (Object.keys(certErrors).length > 0) errors.certifications = certErrors;

  if (!data.languages[0])
    errors.languages = "At least one language is required";

  return errors;
};

export interface RoleReportingErrors {
  role?: string;
  jobType?: string;
  joiningDate?: string;
  reportingManagerName?: string;
}

export const validateRoleReporting = (
  data: RoleReportingData,
): RoleReportingErrors => {
  const errors: RoleReportingErrors = {};

  if (!data.role) errors.role = "Role is required";
  if (!data.jobType) errors.jobType = "Job type is required";
  if (!data.joiningDate) errors.joiningDate = "Joining date is required";
  if (!data.reportingManagerName)
    errors.reportingManagerName = "Reporting manager is required";

  return errors;
};

export const createId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const initialBasicInfo: BasicInfoData = {
  photo: null,
  photoPreview: null,
  title: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  dob: "",
  gender: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  documents: [],
};

export const initialEducationWork: EducationWorkData = {
  qualifications: [{ id: createId(), qualification: "", collegeName: "" }],
  workExperience: "",
  specialisations: [{ id: createId(), specialisation: "", yearsInArea: "" }],
  expertises: [{ id: createId(), expertise: "" }],
  certifications: [{ id: createId(), certification: "" }],
  languages: ["", "", "", ""],
};

export const initialRoleReporting: RoleReportingData = {
  role: "",
  jobType: "",
  joiningDate: "",
  reportingManagerName: "",
  permissions: { actions: {}, content: {} },
};
